// src/components/analytics/InventoryIntelligenceDashboard.js
// --- UPDATE (ML Ops) ----------------------------------------------------------------
//   • "Export Report" did nothing (it only showed a toast). It now downloads a CSV report
//     with the KPI summary, low-stock alerts, turnover by category and monthly consumption —
//     exactly the data on screen — and says so when there is nothing to export.
//   • Results are ignored after unmount; Refresh no longer double-toasts on failure.
// --- END UPDATE ---------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Alert,
  AlertIcon,
  Heading,
  VStack,
  HStack,
  Button,
  Icon,
  useToast,
  Badge,
  useColorModeValue,
  Text,
  Flex,
  Progress,
  StatHelpText,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { FaSync, FaDownload, FaBoxes, FaExclamationTriangle, FaChartLine } from 'react-icons/fa';
import analyticsService from '../../services/analyticsService';
import InventoryValuationWidget from '../dashboard/widgets/InventoryValuationWidget';
import LowStockWidget from '../dashboard/widgets/LowStockWidget';
import ABCAnalysisWidget from './ABCAnalysisWidget';
import DynamicStockLevelsWidget from './DynamicStockLevelsWidget';
import SupplierPerformanceWidget from './SupplierPerformanceWidget';
import PartsConsumptionWidget from './PartsConsumptionWidget';


const StatCard = ({ title, stat, isLoading, helpText, icon, alert, trend }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const alertBg = useColorModeValue('orange.50', 'orange.900');

  return (
    <Box
      p={5}
      shadow="md"
      borderWidth="1px"
      borderRadius="lg"
      bg={alert ? alertBg : cardBg}
      borderColor={alert ? 'orange.500' : borderColor}
      transition="all 0.3s"
      _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
    >
      <Stat>
        <Flex justify="space-between" align="flex-start">
          <Box flex="1">
            <StatLabel fontSize="sm" fontWeight="medium">
              {title}
            </StatLabel>
            {isLoading ? (
              <Spinner size="sm" mt={2} />
            ) : (
              <StatNumber fontSize="2xl" fontWeight="bold" mt={2}>
                {stat}
              </StatNumber>
            )}
            {helpText && (
              <StatHelpText fontSize="xs" mt={1}>
                {helpText}
              </StatHelpText>
            )}
          </Box>
          {icon && (
            <Icon
              as={icon}
              w={8}
              h={8}
              color={alert ? 'orange.500' : 'blue.500'}
              opacity={0.6}
            />
          )}
        </Flex>
        {alert && (
          <Badge colorScheme="orange" mt={2} fontSize="xs">
            Action Required
          </Badge>
        )}
        {trend && (
          <Progress
            value={trend}
            size="xs"
            colorScheme={trend > 75 ? 'green' : trend > 50 ? 'yellow' : 'red'}
            mt={2}
            borderRadius="full"
          />
        )}
      </Stat>
    </Box>
  );
};

function InventoryIntelligenceDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [consumptionData, setConsumptionData] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [turnoverData, setTurnoverData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const toast = useToast();
  // --- UPDATE (ML Ops)
  const isMounted = useRef(true);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);
  // --- END UPDATE
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [metricsRes, consumptionRes, stockAlertsRes, turnoverRes] = await Promise.all([
        analyticsService.getWidgetData('inventory-metrics'),
        analyticsService.getWidgetData('parts-consumption'),
        analyticsService.getWidgetData('low-stock-alerts'),
        analyticsService.getWidgetData('inventory-turnover'),
      ]);
      if (!isMounted.current) return; // --- UPDATE (ML Ops)

      setMetrics(metricsRes?.data?.data ?? { 
        total_inventory_value: 0, 
        total_items: 0,
        low_stock_items: 0,
        turnover_rate: 0 
      });
      
      setConsumptionData(Array.isArray(consumptionRes?.data?.data) ? consumptionRes.data.data : []);
      setStockAlerts(Array.isArray(stockAlertsRes?.data?.data) ? stockAlertsRes.data.data : []);
      setTurnoverData(Array.isArray(turnoverRes?.data?.data) ? turnoverRes.data.data : []);
      
      setLastRefresh(new Date());
    } catch (err) {
      if (!isMounted.current) return; // --- UPDATE (ML Ops)
      const errorMsg = 'Failed to fetch inventory analytics.';
      setError(errorMsg);
      console.error('Inventory data fetch error:', err);
      toast({
        title: 'Error Loading Data',
        description: errorMsg,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    toast({
      title: 'Refreshing Inventory Data',
      description: 'Updating all inventory metrics...',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
    await fetchData();
    if (isMounted.current) setRefreshing(false);
  };

  // --- UPDATE (ML Ops): real CSV export of everything on screen
  const handleExport = () => {
    const esc = (v) => {
      const t = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const table = (title, rows) => {
      if (!rows.length) return [`${title}`, 'No data', ''];
      const cols = [...new Set(rows.flatMap(r => Object.keys(r || {})))].filter(k => typeof rows[0][k] !== 'object' || rows[0][k] === null);
      return [title, cols.map(esc).join(','), ...rows.map(r => cols.map(c => esc(r?.[c])).join(',')), ''];
    };
    const hasData = metrics || stockAlerts.length || turnoverData.length || consumptionData.length;
    if (!hasData) {
      toast({ title: 'Nothing to export', description: 'Load inventory data first.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    const lines = [
      `FlowSense Inventory Intelligence Report,${esc(new Date().toLocaleString())}`,
      '',
      'Summary',
      'Metric,Value',
      `Total inventory value,${esc(metrics?.total_inventory_value ?? 0)}`,
      `Total items (SKUs),${esc(metrics?.total_items ?? 0)}`,
      `Low stock items,${esc(metrics?.low_stock_items ?? 0)}`,
      `Turnover rate (annual),${esc(metrics?.turnover_rate ?? 0)}`,
      '',
      ...table('Low stock alerts', stockAlerts),
      ...table('Stock turnover by category', turnoverData),
      ...table('Monthly consumption', consumptionData),
    ];
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flowsense-inventory-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast({ title: 'Report exported', description: 'The CSV includes the summary, low-stock alerts, turnover and consumption.', status: 'success', duration: 3000, isClosable: true });
  };
  // --- END UPDATE

  if (error && !metrics) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">Error Loading Dashboard</Text>
          <Text fontSize="sm">{error}</Text>
        </Box>
        <Button size="sm" ml="auto" onClick={handleRefresh}>
          Retry
        </Button>
      </Alert>
    );
  }

  const lowStockPercentage = metrics?.total_items > 0 
    ? ((metrics?.low_stock_items / metrics?.total_items) * 100).toFixed(1)
    : 0;

  return (
    <VStack spacing={8} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center" flexWrap="wrap">
        <Box>
          <Heading as="h2" size="lg">
            Inventory Intelligence
          </Heading>
          {lastRefresh && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Text>
          )}
        </Box>
        <HStack spacing={2}>
          <Button
            leftIcon={<Icon as={FaSync} />}
            size="sm"
            onClick={handleRefresh}
            isLoading={refreshing}
            variant="outline"
          >
            Refresh
          </Button>
          <Button
            leftIcon={<Icon as={FaDownload} />}
            size="sm"
            onClick={handleExport}
            colorScheme="blue"
          >
            Export Report
          </Button>
        </HStack>
      </Flex>

      {/* Alert Banner */}
      {metrics?.low_stock_items > 0 && (
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="bold">Low Stock Alert</Text>
            <Text fontSize="sm">
              {metrics.low_stock_items} item{metrics.low_stock_items !== 1 ? 's are' : ' is'} below reorder point. 
              Immediate action required to prevent stockouts.
            </Text>
          </Box>
        </Alert>
      )}

      {/* KPI Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <StatCard
          title="Total Inventory Value"
          stat={metrics?.total_inventory_value 
            ? `$${Number(metrics.total_inventory_value).toLocaleString()}`
            : '$0'}
          helpText="Current inventory worth"
          isLoading={loading}
          icon={FaBoxes}
        />
        
        <StatCard
          title="Total Items"
          stat={metrics?.total_items ?? 0}
          helpText="Unique SKUs in stock"
          isLoading={loading}
          icon={FaChartLine}
        />
        
        <StatCard
          title="Low Stock Items"
          stat={metrics?.low_stock_items ?? 0}
          helpText={`${lowStockPercentage}% of total inventory`}
          isLoading={loading}
          icon={FaExclamationTriangle}
          alert={metrics?.low_stock_items > 0}
          trend={100 - lowStockPercentage}
        />
        
        <StatCard
          title="Turnover Rate"
          stat={metrics?.turnover_rate 
            ? `${Number(metrics.turnover_rate).toFixed(2)}x`
            : '0x'}
          helpText="Annual inventory turns"
          isLoading={loading}
          icon={FaSync}
        />
      </SimpleGrid>

      {/* Tabbed Content */}
      <Tabs
        index={activeTab}
        onChange={setActiveTab}
        variant="enclosed"
        colorScheme="blue"
      >
        <TabList>
          <Tab>Overview</Tab>
          <Tab>ABC Analysis</Tab>
          <Tab>Consumption & Trends</Tab>
          <Tab>Supplier Performance</Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <Box
                p={5}
                shadow="md"
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                borderColor={borderColor}
              >
                <Heading as="h3" size="md" mb={4}>
                  Inventory Valuation
                </Heading>
                <InventoryValuationWidget />
              </Box>

              <Box
                p={5}
                shadow="md"
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                borderColor={borderColor}
              >
                <Heading as="h3" size="md" mb={4}>
                  Low Stock Alerts
                </Heading>
                <LowStockWidget />
              </Box>

              <Box
                p={5}
                shadow="md"
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                borderColor={borderColor}
                gridColumn={{ base: 1, lg: 'span 2' }}
              >
                <Heading as="h3" size="md" mb={4}>
                  Stock Turnover by Category
                </Heading>
                {loading ? (
                  <Flex justify="center" align="center" h="300px">
                    <Spinner />
                  </Flex>
                ) : turnoverData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={turnoverData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="turnover_rate" fill="#3182CE" name="Turnover Rate" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Flex justify="center" align="center" h="300px">
                    <Text color="gray.500">No turnover data available</Text>
                  </Flex>
                )}
              </Box>
            </SimpleGrid>
          </TabPanel>

          {/* ABC Analysis Tab */}
          <TabPanel px={0}>
            <ABCAnalysisWidget />
          </TabPanel>

          {/* Consumption & Trends Tab */}
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <PartsConsumptionWidget />
              <DynamicStockLevelsWidget />
              
              <Box
                p={5}
                shadow="md"
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                borderColor={borderColor}
                gridColumn={{ base: 1, lg: 'span 2' }}
              >
                <Heading as="h3" size="md" mb={4}>
                  Monthly Consumption Trend
                </Heading>
                {loading ? (
                  <Flex justify="center" align="center" h="300px">
                    <Spinner />
                  </Flex>
                ) : consumptionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={consumptionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="total_used" 
                        stroke="#3182CE" 
                        strokeWidth={2}
                        name="Parts Used" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Flex justify="center" align="center" h="300px">
                    <Text color="gray.500">No consumption data available</Text>
                  </Flex>
                )}
              </Box>
            </SimpleGrid>
          </TabPanel>

          {/* Supplier Performance Tab */}
          <TabPanel px={0}>
            <SupplierPerformanceWidget />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}

export default InventoryIntelligenceDashboard;